// PageDescription: 매거진 상세 페이지
import { dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import React from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import MagazineDetailTemplate from '@/components/templates/communication/magazine/magazine-detail-template';
import { SIX_HOURS_IN_MILLISECONDS } from '@/constants/common';
import { Urls } from '@/constants/url';
import useGetBannerAdExchange from '@/hooks/advertisement/useGetBannerAdExchange';
import useUnifiedAdBanner from '@/hooks/advertisement/useUnifiedAdBanner';
import useAppInstallTopBanner from '@/hooks/cuj/useAppInstallTopBanner';
import useGetMagazineDetail from '@/hooks/magazine/useGetMagazineDetail';
import useGetPrevNextMagazinePid from '@/hooks/magazine/useGetPrevNextMagazinePid';
import useGetRelatedMagazine from '@/hooks/magazine/useGetRelatedMagazine';
import useMagazineDetailViewModel from '@/hooks/magazine/useMagazineDetailViewModel';
import useGetMagazineRelatedQna from '@/hooks/qna/useGetMagazineRelatedQna';
import useCustomRouter from '@/hooks/useCustomRouter';
import { AdPageType } from '@/models/ad/list';
import { ExtendedQueryClient } from '@/models/common/query-client';
import { ssrQueryCache } from '@/models/common/ssr-cache';
import { formatAuthorType } from '@/shared/utils/format';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { GraphqlError, ServerError } from '@/types/error';
import { AuthorType, MagazineDetailVo, MagazineTagType, MagazineType } from '@/types/magazine';
import { convertMarkdownToPlainText } from '@/utils/format';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import dayjs from 'dayjs';

const createMagazineDetailLdJson = (magazineDetail?: MagazineDetailVo) => {
  if (!magazineDetail) {
    return {};
  }

  const aboutFilters = magazineDetail.tags.filter((tag) => tag.tagType === MagazineTagType.DISEASE);

  const isDoctor = magazineDetail.author.authorType === AuthorType.DOCTOR;

  const author = {
    '@type': 'Person',
    name: magazineDetail.author.name,
    ...(magazineDetail.author.profileDescription && {
      description: magazineDetail.author.profileDescription,
    }),
    jobTitle: formatAuthorType(magazineDetail.author.authorType),
    url: `${process.env.NEXT_PUBLIC_DOMAIN}/author/${magazineDetail.author.authorPublicId}`,
    image: isDoctor ? magazineDetail.author.profileImageUrl : 'https://media.doctornow.co.kr/og-qna.png',
    affiliation: {
      '@type': 'Organization',
      name: magazineDetail.author.affiliatedInstitutionName,
    },
    ...(magazineDetail.author.careers.length > 0 && {
      alumniOf: magazineDetail.author.careers.map((career) => ({
        '@type': 'Organization',
        name: career.content,
      })),
    }),
  };

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MAGAZINE.DETAIL.path}/${magazineDetail.magazinePid}`,
      headline: magazineDetail.title,
      name: '닥터나우 건강매거진',
      description: magazineDetail.description,
      articleBody: convertMarkdownToPlainText(magazineDetail.content),
      image: [magazineDetail?.titleImageUrl],
      datePublished: dayjs(magazineDetail?.createdAt).format('YYYY-MM-DDTHH:mm:ss[Z]'),
      author: author,
      publisher: {
        '@type': 'Organization',
        name: '닥터나우',
        url: `${process.env.NEXT_PUBLIC_DOMAIN}`,
      },
      url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MAGAZINE.DETAIL.path}/${magazineDetail.magazinePid}`,
      ...(aboutFilters.length > 0 && {
        about: aboutFilters.map((tag) => {
          return {
            '@type': 'MedicalCondition',
            name: tag.name,
          };
        }),
        keywords: magazineDetail.tags.map((tag) => tag.name).join(', '),
      }),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      image: [magazineDetail?.titleImageUrl],
      headline: magazineDetail.title,
      name: '닥터나우 건강매거진',
      author: author,
      url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MAGAZINE.DETAIL.path}/${magazineDetail.magazinePid}`,
      mainEntity: {
        '@type': 'Article',
        '@id': `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MAGAZINE.DETAIL.path}/${magazineDetail.magazinePid}`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '닥터나우',
      url: `${process.env.NEXT_PUBLIC_DOMAIN}`,
      logo: 'https://media.doctornow.co.kr/og-main.png',
      contactPoint: [
        {
          '@type': 'ContactPoint',
          email: 'contact@doctornow.co.kr',
          contactType: 'customer service',
        },
      ],
    },
  ];
};

interface Props extends CustomPageProps {
  id: string;
}

const MagazineDetail = ({ id, client }: Props) => {
  const { router } = useCustomRouter();
  const viewModel = useMagazineDetailViewModel({
    magazinePid: id,
    client,
    missionId: router.query.missionId as string | undefined,
  });
  const topBannerViewModel = useAppInstallTopBanner();
  const magazineDetail = viewModel.magazine;
  const ldJson = createMagazineDetailLdJson(viewModel.magazine);

  const hasJapaneseContent = magazineDetail?.hasJapanese && magazineDetail?.contentType === MagazineType.ARTICLE;
  const alternateKR = [
    {
      hrefLang: 'ko',
      href: `https://doctornow.co.kr/content/magazine/${id}`,
    },
    {
      hrefLang: 'ko-KR',
      href: `https://doctornow.co.kr/content/magazine/${id}`,
    },
    {
      hrefLang: 'x-default',
      href: `https://doctornow.co.kr/content/magazine/${id}`,
    },
  ];

  const alternateJP = [
    {
      hrefLang: 'ja',
      href: `https://doctornow.jp/magazine/${id}`,
    },
    {
      hrefLang: 'ja-JP',
      href: `https://doctornow.jp/magazine/${id}`,
    },
  ];

  return (
    <>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />
      </Head>
      <NextSeo
        title={`${magazineDetail?.title} - 닥터나우`}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MAGAZINE.DETAIL.path}/${id}`}
        description={`${magazineDetail?.description}`}
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_DOMAIN}${Urls.Magazine}/${id}`,
          title: magazineDetail?.title || '',
          description: magazineDetail?.description || '',
          images: [
            {
              url: magazineDetail?.titleImageUrl || '',
              alt: 'Og Image Alt',
            },
          ],
        }}
        twitter={{
          handle: '@doctornow',
          site: '@doctornow.co.kr',
          cardType: 'summary_large_image',
        }}
        languageAlternates={hasJapaneseContent ? [...alternateKR, ...alternateJP] : alternateKR}
      />
      <MagazineDetailTemplate viewModel={viewModel} topBannerViewModel={topBannerViewModel} client={client} />
    </>
  );
};

MagazineDetail.Layout = DefaultLayout;
export default MagazineDetail;

export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (ctx) => {
  const id = ctx.params?.id as string;

  if (!id) {
    return {
      redirect: {
        destination: ROUTES.MAGAZINE.LIST.path + '/INTERNAL',
        permanent: true,
      },
    };
  }

  const queryClient = new ExtendedQueryClient();

  const cacheKey = ctx.req.url as string;

  try {
    if (ssrQueryCache.has(cacheKey)) {
      queryClient.setQueryDataFromCache(ssrQueryCache.get(cacheKey) ?? []);
      await Promise.allSettled([
        useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.TopCtaBand, ctx.client),
        useUnifiedAdBanner.prefetchQuery(queryClient, AdPageType.MagazineDetail, ctx.client),
        useUnifiedAdBanner.prefetchQuery(queryClient, AdPageType.TopCommonBanner, ctx.client),
      ]);

      return {
        props: {
          id,
          dehydratedState: dehydrate(queryClient),
        },
      };
    }

    await Promise.allSettled([
      useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.TopCtaBand, ctx.client),
      useUnifiedAdBanner.prefetchQuery(queryClient, AdPageType.TopCommonBanner, ctx.client),
      useUnifiedAdBanner.prefetchQuery(queryClient, AdPageType.MagazineDetail, ctx.client),
      useGetMagazineDetail.prefetchQuery(queryClient, id),
      useGetPrevNextMagazinePid.prefetchQuery(queryClient, {
        magazinePid: id,
        contentType: MagazineType.ARTICLE,
      }),
      useGetMagazineRelatedQna.prefetchQuery(queryClient, id),
      useGetRelatedMagazine.prefetchQuery(queryClient, id),
    ]);

    ssrQueryCache.set(
      cacheKey,
      queryClient.getQueryClientData([
        useGetMagazineDetail.getKey(id),
        useGetPrevNextMagazinePid.getKey({ magazinePid: id, contentType: MagazineType.ARTICLE }),
        useGetMagazineRelatedQna.getKey(id),
        useGetRelatedMagazine.getKey(id),
      ]),
      SIX_HOURS_IN_MILLISECONDS
    );

    return {
      props: {
        id,
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      },
    };
  } catch (e) {
    const { code } = e as unknown as GraphqlError;
    if (code === ServerError.NotFound) {
      return {
        notFound: true,
      };
    }
    if (ctx.res && code === ServerError.BadRequest) {
      ctx.res.statusCode = 400;
    }

    return {
      notFound: true,
    };
  }
});
